// Crew leaderboard mini — who's on top this month (Ship 3, Aug 26 2026).
//
// Reads `crew_leaderboard` (the same view the Crew board ranks from), so the
// podium here and the podium there can't disagree. Month, not week: a Monday
// morning week-to-date podium is mostly noise. Activity only — no money.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

const MEDALS = ["🥇", "🥈", "🥉"];

interface CrewRow {
  user_id: string;
  full_name: string | null;
  role: string | null;
  points_month: number | null;
  apps_month: number | null;
  claims_month: number | null;
  builds_month: number | null;
}

// Integers render bare, halves keep one decimal (split-deal credit is 0.5).
const fmtHalf = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));

export default function LeaderboardWidget() {
  const [rows, setRows] = useState<CrewRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("crew_leaderboard")
        .select("user_id,full_name,role,points_month,apps_month,claims_month,builds_month")
        .eq("is_active", true)
        .order("points_month", { ascending: false, nullsFirst: false })
        .limit(3);
      if (cancelled) return;
      if (error || !data) {
        setFailed(true);
        return;
      }
      setRows(data as CrewRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🏆</span>
        <span className="font-bold text-sm text-bardin-ink">Crew leaderboard</span>
        <span className="ml-auto text-xs font-bold text-slate-500">this month</span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the leaderboard — try a refresh.</div>
      ) : !rows ? (
        <div className="space-y-2 mt-1">
          <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No crew activity logged this month yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <Link
              key={r.user_id}
              to="/crew"
              onClick={() => logCardClick("widget_leaderboard_row")}
              className="flex items-center gap-2 py-1.5"
            >
              <span className="text-sm w-5">{MEDALS[i] ?? ""}</span>
              <span className="text-[13px] font-bold text-bardin-ink truncate flex-1">
                {r.full_name ?? "—"}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold whitespace-nowrap hidden sm:inline">
                {fmtHalf(Number(r.apps_month ?? 0))}a · {fmtHalf(Number(r.claims_month ?? 0))}c ·{" "}
                {fmtHalf(Number(r.builds_month ?? 0))}b
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-extrabold whitespace-nowrap">
                {Math.round(Number(r.points_month ?? 0))} pts
              </span>
            </Link>
          ))}
        </div>
      )}

      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/crew"
          onClick={() => logCardClick("widget_leaderboard_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Crew board ›
        </Link>
      </div>
    </div>
  );
}
